import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlanningSection, SectionUpdateEvent } from '~/types/overview';
import type { ConversationDocument } from '~/hooks/useConversationDocuments';
import type { BirtvyDoc } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import { DOC_W, DOC_H } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import { outlineToMarkdown, writeCanvasToOutline } from '~/design-system/components/DeckGen/components/editor/canvasDocsUtils';
import { documentItems } from '../directory/toDirectoryItems';
import { itemKey } from '../ids';
import type { DeckContent, DeckEntity } from '../deckContent';
import type { HostSinks } from './sseHandlers';
import { planningDocs, readablePlanningContent, resourceDocId, sectionText } from './workspaceContent';
import type { DocEditPatch } from '../preview/docEditBus';

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: 'include', ...init });
  if (!response.ok) throw new Error(`Workspace request failed (${response.status})`);
  return response.json();
}

/**
 * The Library query's key, exported so a WRITE can invalidate what this READ projects.
 *
 * It matters because the projection below is authoritative: a blob doc whose `documentId` is not in
 * the cached library is filtered OUT — the rule that stops a deleted document lingering as a card.
 * A freshly created one is indistinguishable from that until the query refetches, so `+ document`
 * wrote the row, wrote the card, and showed nothing at all.
 */
export const libraryQueryKey = (conversationId: string | undefined) =>
  ['deck15', 'library', conversationId] as const;

/** The directory projects the original Goal/outline/Library records; it never creates duplicate originals. */
export function useWorkspaceResources(conversationId: string | undefined, base: DeckContent,
  focus: (key: string) => void) {
  const client = useQueryClient();
  const sectionsKey = useMemo(() => ['planningSections', conversationId], [conversationId]);
  const libraryKey = useMemo(() => libraryQueryKey(conversationId), [conversationId]);
  const sectionsUrl = `/planning-sections-api/${encodeURIComponent(conversationId ?? '')}`;
  const libraryUrl = `/documents-api/${encodeURIComponent(conversationId ?? '')}`;
  const { data: sections = [] } = useQuery({ queryKey: sectionsKey, enabled: !!conversationId,
    queryFn: () => json<PlanningSection[]>(sectionsUrl) });
  const { data: library = [], isSuccess: libraryLoaded } = useQuery({ queryKey: libraryKey, enabled: !!conversationId,
    queryFn: async () => { const data = await json<ConversationDocument[] | { documents: ConversationDocument[] }>(libraryUrl);
      return Array.isArray(data) ? data : data.documents; } });
  const [drafts, setDrafts] = useState<Record<string, { title: string; content: string }>>({});
  const focusedDrafts = useRef(new Set<string>());

  const resources = useMemo(() => {
    const docs: Array<{ key: string; title: string; content: string; documentId?: string }> = planningDocs(sections)
      .map(doc => ({ ...doc, documentId: library.find(row => row.planKey === doc.key)?.id }));
    const outline = sections.find(section => section.sectionType === 'OUTLINE');
    if (outline) docs.push({ key: 'outline', title: 'Outline', content: readablePlanningContent(outlineToMarkdown(outline.pendingContent || outline.content || '')) });
    for (const doc of library) {
      if (doc.planKey && docs.some(resource => resource.key === doc.planKey)) continue;
      docs.push({ key: doc.planKey || `document:${doc.id}`, title: doc.title, content: doc.content, documentId: doc.id });
    }
    for (const [key, draft] of Object.entries(drafts)) {
      const at = docs.findIndex(doc => doc.key === key);
      if (at < 0) docs.push({ key, ...draft }); else docs[at] = { ...docs[at], ...draft };
    }
    return docs;
  }, [sections, library, drafts]);

  const projected = useMemo(() => {
    const docs = new Map<string, DeckEntity>();
    const keys = new Map<string, string>();
    for (const resource of resources) {
      const documentId = resource.documentId || (resource.key.startsWith('document:') ? resource.key.slice(9) : undefined);
      const existing = base.items.documents.map(row => base.entity(row.id)).find(entity => entity?.kind === 'documents' &&
        (documentId ? entity.doc.documentId === documentId : entity.doc.resourceKey === resource.key || entity.doc.id === base.planningDocumentIds?.[resource.key]));
      const id = existing?.kind === 'documents' ? existing.doc.id : resourceDocId(resource.key);
      const doc: BirtvyDoc = { id, x: 0, y: 0, width: DOC_W, height: DOC_H, title: resource.title,
        content: resource.content, documentId, resourceKey: resource.key, streaming: !!drafts[resource.key] };
      const key = itemKey('documents', id);
      docs.set(key, { kind: 'documents', doc }); keys.set(resource.key, key);
      if (documentId) keys.set(`document:${documentId}`, key);
    }
    const added = documentItems([...docs.values()].flatMap(entity => entity.kind === 'documents' ? [entity.doc] : []));
    const overridden = new Set(added.map(row => row.id));
    const content: DeckContent = { ...base,
      items: { ...base.items, documents: [...base.items.documents.filter(row => {
        const entity = base.entity(row.id);
        const libraryId = entity?.kind === 'documents' ? entity.doc.documentId : undefined;
        return !overridden.has(row.id) && (!libraryLoaded || !libraryId || library.some(doc => doc.id === libraryId));
      }), ...added] },
      entity: key => docs.get(key) ?? base.entity(key) };
    return { content, keys };
  }, [base, resources, drafts, libraryLoaded, library]);
  const current = useRef({ sections, projected, focus, library });
  current.current = { sections, projected, focus, library };
  const openResource = useCallback((key: string) => {
    current.current.focus(current.current.projected.keys.get(key) ?? itemKey('documents', resourceDocId(key)));
  }, []);

  const sectionUpdate = useCallback((event: SectionUpdateEvent) => {
    const before = new Map(planningDocs(current.current.sections).map(doc => [doc.key, doc.content]));
    const after = planningDocs([{ id: event.sectionType, ...event }]);
    const changed = after.filter(doc => before.get(doc.key) !== doc.content);
    client.setQueryData<PlanningSection[]>(sectionsKey, (old = []) => [
      ...old.filter(section => section.sectionType !== event.sectionType),
      { id: old.find(section => section.sectionType === event.sectionType)?.id ?? event.sectionType,
        ...event },
    ]);
    setDrafts(old => { const next = { ...old }; if (event.sectionType === 'GOAL') delete next.goal; return next; });
    openResource(event.sectionType === 'GOAL' ? 'goal' : changed.at(-1)?.key || 'outline');
  }, [client, sectionsKey, openResource]);

  const documentUpdated = useCallback<HostSinks['documentUpdated']>(event => {
    client.setQueryData<ConversationDocument[]>(libraryKey, (old = []) => {
      const existing = old.find(doc => doc.id === event.documentId);
      return [...old.filter(doc => doc.id !== event.documentId), { ...existing, id: event.documentId,
        conversationId: conversationId ?? '', title: event.title, content: event.content,
        status: 'ACTIVE', origin: 'AGENT', createdAt: existing?.createdAt ?? null, updatedAt: null }];
    });
    setDrafts(old => Object.fromEntries(Object.entries(old).filter(([key, draft]) =>
      key !== `document:${event.documentId}` && draft.title !== event.title)));
    openResource(`document:${event.documentId}`);
  }, [client, libraryKey, conversationId, openResource]);

  const host = useMemo<Partial<HostSinks>>(() => ({
    sectionUpdate, documentUpdated,
    documentProposal: event => {
      documentUpdated(event);
      if (event.status === 'PROPOSED') void json(`${libraryUrl}/${event.documentId}/accept`, { method: 'POST' })
        .then(() => client.invalidateQueries({ queryKey: libraryKey }))
        .catch(error => console.error('Could not accept the generated document', error));
    },
    documentDeleted: event => {
      client.setQueryData<ConversationDocument[]>(libraryKey, (old = []) => old.filter(doc => doc.id !== event.documentId));
    },
    resourceDraft: draft => {
      const key = draft.key.startsWith('document:') ? current.current.library.find(doc => doc.id === draft.key.slice(9))?.planKey || draft.key : draft.key;
      setDrafts(old => ({ ...old, [key]: { ...draft, key } }));
      if (!focusedDrafts.current.has(key)) { focusedDrafts.current.add(key); openResource(key); }
    },
    streamFinished: () => {
      setDrafts({}); focusedDrafts.current.clear();
      void client.invalidateQueries({ queryKey: sectionsKey });
      void client.invalidateQueries({ queryKey: libraryKey });
    },
    sourcesUpdated: () => { void client.invalidateQueries({ queryKey: libraryKey }); },
    chapterUpdated: event => openResource(`canvas:${event.canvasId}`),
  }), [sectionUpdate, documentUpdated, libraryUrl, client, libraryKey, sectionsKey, openResource]);

  const save = useCallback(async (doc: BirtvyDoc, patch: DocEditPatch) => {
    const key = doc.resourceKey;
    if (!key) return false;
    if (key.startsWith('document:')) {
      await json(`${libraryUrl}/${key.slice(9)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      await client.invalidateQueries({ queryKey: libraryKey });
      return true;
    }
    const outline = current.current.sections.find(section => section.sectionType === 'OUTLINE');
    const value = key === 'goal' ? patch.content : key.startsWith('canvas:')
      ? writeCanvasToOutline(outline?.pendingContent || outline?.content || '', key.slice(7), patch.content) : null;
    if (value !== null) {
      await json(`${sectionsUrl}/${key === 'goal' ? 'goal' : 'outline'}`, { method: 'PUT',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content: value }) });
      await client.invalidateQueries({ queryKey: sectionsKey });
    }
    return true;
  }, [libraryUrl, sectionsUrl, client, libraryKey, sectionsKey]);
  const readSections = useCallback(() => json<PlanningSection[]>(sectionsUrl), [sectionsUrl]);
  return { content: projected.content, host, openResource, save, readSections, sections,
    goal: sectionText(sections.find(section => section.sectionType === 'GOAL')) };
}
